import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { ArrowLeft, PaperPlaneTilt, Trash, Timer } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';
import SwornFace from '../components/sworn/SwornFace';
import { Frame } from '../components/shell/AppShell';
import { swornParent } from '../lib/navigation';
import swornArt from '../assets/bg/the-sworn.webp';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const POLL_MS = 8000;
const MAX_CHARS = 4000;

const DURATIONS = [
  { key: '1h', seconds: 3600, label: '1 hour' },
  { key: '24h', seconds: 86400, label: '24 hours' },
  { key: '7d', seconds: 604800, label: '7 days' },
];

const when = (iso) => {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch (e) {
    return '';
  }
};

/**
 * One private thread between two of the Sworn.
 *
 * There is no way to reach this page except by tapping somebody in the circle's
 * member list, and that list is only visible to the circle. That is deliberate
 * and it is the whole of what keeps §2.1 true: this is a room, not a
 * capability. No member search, no "message this member" in the Commons, no
 * addressing anybody by number.
 *
 * Unlike the shared room, the Founder has no powers here. He is not a party to
 * a conversation between two other members, cannot read it, and cannot remove
 * from it. Only the author can take back what they wrote, and it leaves
 * nothing behind — there is nobody else who could have taken it, so there is
 * never a mark to explain.
 *
 * Disappearing messages can be switched by either party, and switching it says
 * so in the thread. What you cannot do in secret, you do not do lightly.
 *
 * See docs/sworn_design.md §2.2, §2.3 and §8.4.
 */
const SwornThread = () => {
  const navigate = useNavigate();
  const { threadId } = useParams();
  const { user } = useAuth();

  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [timerOpen, setTimerOpen] = useState(false);
  const endRef = useRef(null);

  // A 404 anywhere here has one meaning with several causes — not in the
  // circle, not in this thread, or the other party has left and the thread is
  // gone (§8.4). None of them is a state worth rendering. Go back to the room;
  // if that is closed to them too, it sends them on to the dashboard.
  const gone = useCallback(() => navigate('/sworn', { replace: true }), [navigate]);

  const load = useCallback(async (opts = {}) => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/sworn/threads/${threadId}/messages`, { withCredentials: true },
      );
      setMessages(data || []);
    } catch (e) {
      if (e.response?.status === 404) gone();
      else if (!opts.quiet) setError('Could not open this conversation.');
    } finally {
      if (!opts.quiet) setLoading(false);
    }
  }, [threadId, gone]);

  useEffect(() => {
    axios.get(`${API_URL}/api/sworn/threads`, { withCredentials: true })
      .then(({ data }) => {
        const found = (data || []).find((t) => t.id === threadId);
        if (found) setThread(found);
      })
      .catch(() => { /* the message load handles the 404 */ });
    load();
  }, [threadId, load]);

  useEffect(() => {
    const t = setInterval(() => load({ quiet: true }), POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const send = async (e) => {
    e.preventDefault();
    const content = draft.trim();
    if (!content || sending) return;
    setSending(true);
    setError('');
    try {
      const { data } = await axios.post(
        `${API_URL}/api/sworn/threads/${threadId}/messages`,
        { content }, { withCredentials: true },
      );
      setMessages((prev) => [...prev, data]);
      setDraft('');
    } catch (err) {
      if (err.response?.status === 404) gone();
      else setError(err.response?.data?.detail || 'That did not send.');
    } finally {
      setSending(false);
    }
  };

  const remove = async (id) => {
    // No confirmation. The usual reason for pressing this is that something is
    // on screen which should not be, and asking "are you sure?" while it is
    // still visible gets the order the wrong way round.
    try {
      await axios.delete(
        `${API_URL}/api/sworn/threads/${threadId}/messages/${id}`,
        { withCredentials: true },
      );
      setMessages((prev) => prev.filter((m) => m.id !== id));
    } catch (err) {
      setError('That could not be removed.');
    }
  };

  const setTimer = async (seconds) => {
    setTimerOpen(false);
    try {
      const { data } = await axios.patch(
        `${API_URL}/api/sworn/threads/${threadId}/disappearing`,
        { enabled: Boolean(seconds), duration_seconds: seconds || null },
        { withCredentials: true },
      );
      setThread(data);
      load({ quiet: true });   // pick up the note the change just posted
    } catch (err) {
      setError('That setting could not be changed.');
    }
  };

  const otherName = thread?.other?.display || 'A member';
  const activeTimer = thread?.disappearing_messages_enabled
    ? DURATIONS.find((d) => d.seconds === thread.disappearing_duration_seconds)
    : null;
  const parent = swornParent(user);

  return (
    <Frame user={user} background={swornArt} scrim={0.55} activeKey={parent.key} fill>
    <div className="flex-1 flex flex-col min-h-0" data-testid="sworn-thread-page">
      <header className="relative border-b border-border bg-background/70 backdrop-blur-sm px-2 py-2">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="touch-icon" onClick={() => navigate('/sworn')} aria-label="Back to The Sworn" data-testid="thread-back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <SwornFace display={otherName} />
          <div className="flex-1 min-w-0">
            <h1 className="font-heading text-base leading-tight truncate" style={{ color: 'hsl(var(--gold-pale))' }}>{otherName}</h1>
            <p className="text-xs text-muted-foreground" data-testid="thread-status">
              {activeTimer
                ? `Disappears after ${activeTimer.label}`
                : 'Only the two of you'}
            </p>
          </div>
          <div className="relative">
            <Button
              variant="ghost"
              size="touch-icon"
              onClick={() => setTimerOpen((v) => !v)}
              aria-label="Disappearing messages"
              aria-expanded={timerOpen}
              data-testid="thread-timer"
            >
              <Timer
                className="w-5 h-5"
                weight={activeTimer ? 'fill' : 'regular'}
                style={{ color: activeTimer ? 'hsl(var(--gold))' : undefined }}
              />
            </Button>
            {timerOpen && (
              <div
                className="absolute right-0 mt-1 w-60 rounded-xl border border-border bg-popover shadow-lg p-1.5 z-50"
                role="menu"
                data-testid="thread-timer-menu"
              >
                <p className="px-2 pt-1 text-xs font-semibold uppercase tracking-wider" style={{ color: 'hsl(var(--gold))' }}>
                  Disappearing messages
                </p>
                {/* Either of you may change this, and the change is announced
                    in the thread. The protection is not that the other has to
                    agree -- it is that they cannot fail to notice. */}
                <p className="px-2 pb-1.5 pt-0.5 text-xs text-muted-foreground">
                  Either of you may change this. It will be said in the thread.
                </p>
                {DURATIONS.map((d) => (
                  <button
                    key={d.key}
                    type="button"
                    role="menuitemradio"
                    aria-checked={activeTimer?.key === d.key}
                    onClick={() => setTimer(d.seconds)}
                    className={`w-full min-h-[40px] text-left px-2 text-sm rounded-lg hover:bg-accent ${
                      activeTimer?.key === d.key ? 'text-[hsl(var(--gold))]' : ''
                    }`}
                    data-testid={`thread-timer-${d.key}`}
                  >
                    After {d.label}
                  </button>
                ))}
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={!activeTimer}
                  onClick={() => setTimer(null)}
                  className={`w-full min-h-[40px] text-left px-2 text-sm rounded-lg hover:bg-accent ${!activeTimer ? 'text-[hsl(var(--gold))]' : ''}`}
                  data-testid="thread-timer-off"
                >
                  Keep messages
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto px-3 py-3" data-testid="thread-messages">
        {loading ? (
          <p className="text-sm text-muted-foreground text-center py-10" role="status">Opening…</p>
        ) : messages.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-sm text-muted-foreground">
              Nothing here yet. This stays between the two of you.
            </p>
          </div>
        ) : (
          <div className="max-w-2xl mx-auto space-y-3">
            {messages.map((m) => {
              const mine = m.sender?.id && m.sender.id === user?.id;
              if (m.message_type === 'note') {
                // Somebody changed the timer. Quiet and centred, but it is a
                // real message from a real person, and it says who: the
                // point of announcing it is that nobody does it anonymously.
                const by = mine ? 'You' : (m.sender_display || otherName);
                return (
                  <div
                    key={m.id}
                    className="mx-auto max-w-[90%] rounded-lg border border-border/60 bg-card/60 px-3 py-1.5 text-center"
                    data-testid={`thread-note-${m.id}`}
                  >
                    <p className="text-xs text-muted-foreground">
                      <span className="font-medium text-foreground/80" data-testid={`thread-note-by-${m.id}`}>{by}</span>
                      {' · '}
                      {m.content}
                    </p>
                    <p className="text-[10px] text-muted-foreground/80 mt-0.5">{when(m.created_at)}</p>
                  </div>
                );
              }
              return (
                <div
                  key={m.id}
                  className={`flex ${mine ? 'justify-end' : 'justify-start'}`}
                  data-testid={`thread-message-${m.id}`}
                >
                  <div className="max-w-[82%] min-w-0">
                    <div
                      className={[
                        'rounded-2xl border px-3 py-2 backdrop-blur-sm bg-card/90',
                        mine ? 'border-primary/40' : 'border-border',
                      ].join(' ')}
                      style={mine ? { backgroundImage: 'linear-gradient(hsl(var(--gold) / 0.12), hsl(var(--gold) / 0.12))' } : undefined}
                    >
                      {/* anywhere, not break-word: only anywhere shrinks the
                          intrinsic width, which is what stops a long unbroken
                          run widening the row past the screen. */}
                      <p
                        className="text-sm leading-relaxed whitespace-pre-wrap text-foreground"
                        style={{ overflowWrap: 'anywhere' }}
                      >
                        {m.content}
                      </p>
                    </div>
                    <div className={`flex items-center gap-1 mt-0.5 ${mine ? 'justify-end' : ''}`}>
                      <span className="text-[11px] text-muted-foreground">{when(m.created_at)}</span>
                      {/* The author only, at once, and it leaves nothing
                          behind. Visible without hover: a phone has none. */}
                      {mine && (
                        <button
                          type="button"
                          onClick={() => remove(m.id)}
                          className="-my-2 flex h-10 w-10 items-center justify-center text-muted-foreground/70 hover:text-destructive"
                          aria-label="Take back what you wrote"
                          data-testid={`thread-remove-${m.id}`}
                        >
                          <Trash className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={endRef} />
          </div>
        )}
      </div>

      <form onSubmit={send} className="border-t border-border bg-card/90 p-3" data-testid="thread-composer">
        <div className="max-w-2xl mx-auto">
          {error && <p role="alert" className="text-sm text-destructive mb-2" data-testid="thread-error">{error}</p>}
          <div className="flex items-end gap-2">
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send(e);
                }
              }}
              placeholder={`Write to ${otherName}…`}
              aria-label={`Write to ${otherName}`}
              rows={1}
              maxLength={MAX_CHARS}
              className="min-h-[44px] max-h-40 resize-none"
              data-testid="thread-input"
            />
            <Button
              type="submit"
              disabled={sending || !draft.trim()}
              className="h-11 w-11 shrink-0 bg-primary hover:bg-primary/90"
              aria-label="Send"
              data-testid="thread-send"
            >
              <PaperPlaneTilt className="w-4 h-4" weight="fill" />
            </Button>
          </div>
        </div>
      </form>
    </div>
    </Frame>
  );
};

export default SwornThread;
